import { useNavigate } from "@entry/navigation";
import { useState } from "react";

import { Sparkles } from "@assets";
import { Loader } from "@components/Loader";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { TaxGainHarvestOptions } from "@components/TaxGainHarvest";
import { TextHighlighter } from "@components/TextHighlighter";
import { BodySmall, Caption } from "@components/Typography";
import { ALPACA_CUSTOMER_AGREEMENT_URL } from "@constants/documents";
import { BeneficiaryType, Flow, GoalType, Routes } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import {
  useErrorBanner,
  useGoalFlow,
  useHarvestAccountDefaultConfigs,
  useOnboarding,
  usePersistSidecarGoal,
  useUser,
} from "@hooks";
import { i18n, Translations } from "@i18n";
import { useStyles } from "./styles";
import { TaxGainHarvestRouteContentProps } from "./types";

export const TaxGainHarvestRoute = () => {
  const { harvestAccountDefaultConfigs, loading } = useHarvestAccountDefaultConfigs();

  if (loading || !harvestAccountDefaultConfigs) {
    return <Loader />;
  }

  return <TaxGainHarvestRouteContent defaultConfigs={harvestAccountDefaultConfigs} />;
};

const TaxGainHarvestRouteContent = ({ defaultConfigs }: TaxGainHarvestRouteContentProps) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const {
    goalProps: {
      beneficiaryId,
      beneficiaryType = BeneficiaryType.Child,
      flow = Flow.Onboarding,
      goalType,
    },
    setGoalProps,
  } = useGoalFlow();

  const { beneficiaries } = useUser();
  const { resetToNext } = useOnboarding();
  const { showErrorBanner } = useErrorBanner();
  const { persistSidecarGoal, loading: persistingSidecarGoal } = usePersistSidecarGoal();

  const beneficiary = beneficiaries?.find((b) => b?.id === beneficiaryId);
  const taxIdProvided = Boolean(beneficiary?.taxIdProvided);

  const [standardDeduction, setStandardDeduction] = useState(
    defaultConfigs.harvestStandardDeduction,
  );
  const [longTermCapitalGains, setLongTermCapitalGains] = useState(
    defaultConfigs.harvestLongTermCapitalGains,
  );

  const handleContinue = async () => {
    setGoalProps({
      harvestStandardDeduction: standardDeduction,
      harvestLongTermCapitalGains: longTermCapitalGains,
      harvestRecommendedFloorUsd: defaultConfigs.harvestRecommendedFloorUsd,
    });

    if (beneficiaryType === BeneficiaryType.Child && !taxIdProvided) {
      navigate(Routes.BeneficiarySsn);
      return;
    }

    // A sidecar goal already has its recurring deposit and target, so it is
    // created here rather than on a deposit route.
    if (goalType === GoalType.TrumpAccountSidecar) {
      try {
        await persistSidecarGoal({
          harvestStandardDeduction: standardDeduction,
          harvestLongTermCapitalGains: longTermCapitalGains,
          harvestRecommendedFloorUsd: defaultConfigs.harvestRecommendedFloorUsd,
        });

        if ([Flow.AddGoal, Flow.AcceptSharedGoal, Flow.Accounts].includes(flow)) {
          navigate(Routes.Accounts);
        } else {
          resetToNext();
        }
      } catch (e: any) {
        showErrorBanner(e.graphQLErrors?.[0]?.message);
      }

      return;
    }

    if ([Flow.AddGoal, Flow.AcceptSharedGoal].includes(flow as Flow)) {
      navigate(Routes.GoalDeposit);
      return;
    }

    navigate(Routes.ChildDeposit);
  };

  const handleDisclosuresPress = () => {
    window.open(ALPACA_CUSTOMER_AGREEMENT_URL, "_blank", "noopener,noreferrer");
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.TaxGainHarvestRouteTitle),
    subtitle: i18n.t(Translations.TaxGainHarvestRouteSubtitle),
    subtitleStyle: styles.subtitle,
    hideLogout: flow !== Flow.Onboarding,
    cta: {
      primary: {
        title: i18n.t(Translations.TaxGainHarvestRoutePrimaryCTA),
        onClick: handleContinue,
        loading: persistingSidecarGoal,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      <div style={styles.benefitCard}>
        <Sparkles size={18} style={styles.benefitIcon} />
        <Caption style={styles.benefitCaption}>
          {i18n.t(Translations.TaxGainHarvestRouteBenefitCallout)}
        </Caption>
      </div>
      <Spacing size={SpacingSizes.S} />
      <TaxGainHarvestOptions
        standardDeduction={standardDeduction}
        longTermCapitalGains={longTermCapitalGains}
        onStandardDeductionChange={setStandardDeduction}
        onLongTermCapitalGainsChange={setLongTermCapitalGains}
      />
      <Spacing size={SpacingSizes.M} />
      <div style={styles.legalSection}>
        <Caption style={styles.footerText}>
          {i18n.t(Translations.TaxGainHarvestRouteFooter)}
        </Caption>
        <Spacing size={SpacingSizes.XS} />
        <div style={styles.disclosureRow}>
          <TextHighlighter
            text={i18n.t(Translations.TaxGainHarvestRouteDisclosureText)}
            words={[
              {
                text: i18n.t(Translations.TaxGainHarvestRouteDisclosureLink),
                onClick: handleDisclosuresPress,
              },
            ]}
            TypographyComponent={BodySmall}
            style={styles.disclosureText}
          />
        </div>
      </div>
      <Spacing size={SpacingSizes.L} />
    </OnboardingLayout>
  );
};
